/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import React from 'react';
import { AIGeneralBlockCard } from '../../AIGeneralBlockCard';
import { Text } from '../../../atoms/Text/Text';
import { useAIAgentMessage } from '../../AIAgentMessage/context';
import './ConfirmationRendererStyles.scss';
function severityToVariant(severity) {
    switch (severity) {
        case 'destructive':
            return 'error';
        case 'warning':
            return 'warning';
        default:
            return 'default';
    }
}
/**
 * Two-column diff table rendered when `before_items` is present on the payload.
 * Each row shows: Field label | Current value | New value.
 * Falls back gracefully: rows where before_items has no matching index show
 * an em-dash for the current value.
 */
function DiffTable({ beforeItems, afterItems }) {
    const usedBeforeIndexes = new Set();
    const resolveBeforeItem = (newItem, index) => {
        const indexed = beforeItems[index];
        if (indexed && indexed.label === newItem.label) {
            usedBeforeIndexes.add(index);
            return indexed;
        }
        const sameLabelIndex = beforeItems.findIndex((candidate, candidateIndex) => candidateIndex !== index &&
            !usedBeforeIndexes.has(candidateIndex) &&
            candidate.label === newItem.label);
        if (sameLabelIndex >= 0) {
            usedBeforeIndexes.add(sameLabelIndex);
            return beforeItems[sameLabelIndex];
        }
        return undefined;
    };
    // Use afterItems as the authoritative row list (it defines which fields changed).
    return (React.createElement("table", { className: "qmb-ui-confirmation-renderer__diff-table" },
        React.createElement("thead", null,
            React.createElement("tr", null,
                React.createElement("th", null, "Field"),
                React.createElement("th", null, "Current"),
                React.createElement("th", null, "New"))),
        React.createElement("tbody", null, afterItems.map((newItem, i) => {
            const oldItem = resolveBeforeItem(newItem, i);
            return (React.createElement("tr", { key: i },
                React.createElement("td", null, newItem.label),
                React.createElement("td", { className: "qmb-ui-confirmation-renderer__diff-table-current" }, oldItem ? oldItem.value : '\u2014'),
                React.createElement("td", { className: "qmb-ui-confirmation-renderer__diff-table-new" }, newItem.value)));
        }))));
}
export function ConfirmationRenderer({ block }) {
    const confirmation = block;
    const { onConfirm, onCancel, isActionTaken } = useAIAgentMessage();
    // Durable resolution: a persisted confirmation that comes back from history
    // already approved/rejected (e.g. after a refresh, or acted on elsewhere) must
    // render in its terminal state — buttons disabled, outcome labeled — so it
    // can't be acted on again. `isActionTaken` covers the live same-session click;
    // `status` covers the rehydrated case.
    const isResolved = confirmation.status === 'approved' || confirmation.status === 'rejected';
    const actionsDisabled = isActionTaken || isResolved;
    const resolvedSection = isResolved
        ? [
            {
                label: undefined,
                content: (React.createElement(Text, { variant: "body", className: "qmb-ui-confirmation-renderer__resolved" }, confirmation.status === 'approved'
                    ? '✓ Approved'
                    : '✕ Canceled'))
            }
        ]
        : [];
    const copyText = [confirmation.title, confirmation.summary, ...confirmation.details].join('\n');
    const cascadeImpactSection = confirmation.cascade_impact && confirmation.cascade_impact.length > 0
        ? [
            {
                label: 'Impact',
                content: (React.createElement("ul", { className: "qmb-ui-confirmation-renderer__cascade-list" }, confirmation.cascade_impact.map((item, i) => (React.createElement("li", { key: i },
                    React.createElement("i", { className: "fa-light fa-triangle-exclamation" }),
                    React.createElement(Text, { variant: "body" }, item))))))
            }
        ]
        : [];
    const severityClass = confirmation.severity !== 'standard'
        ? `qmb-ui-confirmation-renderer--${severityToVariant(confirmation.severity)}`
        : '';
    // Two-column diff mode: use before_items when present AND preview_items is also
    // present (we need both sides). Falls back to single-column rendering otherwise.
    const hasDiff = confirmation.before_items != null &&
        confirmation.before_items.length > 0 &&
        confirmation.preview_items != null &&
        confirmation.preview_items.length > 0;
    const previewSection = hasDiff
        ? [
            {
                label: 'Changes',
                content: (React.createElement(DiffTable, { beforeItems: confirmation.before_items, afterItems: confirmation.preview_items }))
            }
        ]
        : confirmation.preview_items && confirmation.preview_items.length > 0
            ? confirmation.preview_items.map((item, i) => ({
                label: item.label,
                content: (React.createElement(Text, { key: i, variant: "body" }, item.value))
            }))
            : confirmation.details.map((detail, i) => ({
                label: undefined,
                content: (React.createElement(Text, { key: i, variant: "body" }, detail))
            }));
    return (React.createElement(AIGeneralBlockCard, { variant: severityToVariant(confirmation.severity), className: `qmb-ui-confirmation-renderer ${severityClass}`.trim(), sections: [
            {
                label: confirmation.title,
                content: React.createElement(Text, { variant: "body" }, confirmation.summary),
                copyText
            },
            ...previewSection,
            ...cascadeImpactSection,
            ...resolvedSection
        ], footerGroups: [
            {
                actions: [
                    {
                        id: 'confirm',
                        label: confirmation.confirm_label,
                        variant: confirmation.confirm_variant === 'danger' ? 'danger' : 'primary',
                        onClick: () => onConfirm === null || onConfirm === void 0 ? void 0 : onConfirm(confirmation.approval_id),
                        disabled: actionsDisabled
                    },
                    {
                        id: 'cancel',
                        label: confirmation.cancel_label,
                        variant: 'default',
                        onClick: () => onCancel === null || onCancel === void 0 ? void 0 : onCancel(confirmation.approval_id),
                        disabled: actionsDisabled
                    }
                ]
            }
        ] }));
}
